import { useSearchParams } from "react-router-dom";

export interface ChannelOption {
  key: string;
  label: string;
}

/** The channel chosen in the address (`?channel=linkedin`), or null for all channels. */
export function useChannel(): [string | null, (channel: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const channel = params.get("channel") || null;
  const set = (next: string | null) =>
    setParams(
      (p) => {
        const q = new URLSearchParams(p);
        if (next) q.set("channel", next);
        else q.delete("channel");
        return q;
      },
      { replace: true },
    );
  return [channel, set];
}

/**
 * The channel filter on Actions and Performance pages: "All" plus one button per channel. It reads and writes
 * `?channel=` so the choice survives a reload and can be linked to (an account page's summary links in with it).
 */
export function ChannelFilter({ channels, label = "Channel" }: { channels: ChannelOption[]; label?: string }) {
  const [channel, setChannel] = useChannel();
  if (!channels.length) return null;
  const options: (ChannelOption | { key: null; label: string })[] = [{ key: null, label: "All" }, ...channels];
  return (
    <div className="segmented channel-filter" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.key ?? "all"} type="button" className="btn btn-quiet btn-small" aria-pressed={channel === o.key} onClick={() => setChannel(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
